import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useSiteConfig, applyAccent } from "@/contexts/SiteConfigContext";

const FIELDS = [
  ["site_name_primary", "Name (first part)", "Photo"],
  ["site_name_accent", "Name (accent part)", "Moto"],
  ["site_domain", "Primary domain", "photomoto.co.uk"],
  ["vertical_noun", "Vertical", "motorsport"],
  ["categories_strip", "Hero categories strip", "Cars · Road Bikes · …"],
  ["calendar_label", "Calendar label", "Race calendar"],
  ["event_noun", "Event noun", "race weekend"],
  ["hero_line1", "Hero line 1", "Shot at the track."],
  ["hero_line2", "Hero line 2", "Sold in one place."],
  ["contact_email", "Contact email", "hello@photomoto.co.uk"],
];

export default function SiteIdentityCard() {
  const { refresh } = useSiteConfig();
  const [form, setForm] = useState(null);
  const [presets, setPresets] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/public/site-config").then((r) => setForm(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/site-identity/presets").then((r) => setPresets(r.data)).catch(() => {});
  }, []);

  if (!form) {
    return <div className="border-t border-white/10 pt-10"><p className="text-white/50 text-sm">Loading site identity…</p></div>;
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const loadPreset = (key) => {
    if (!key || !presets[key]) return;
    setForm((f) => ({ ...f, ...presets[key] }));
    applyAccent(presets[key].accent_color);
    toast.info(`Loaded "${key}" preset — review then save to activate`);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/admin/site-identity", form);
      setForm((f) => ({ ...f, ...data }));
      refresh();
      toast.success("Site identity saved — branding updated everywhere");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-t border-white/10 pt-10 space-y-6" data-testid="site-identity">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h3 className="font-serif text-2xl">Site identity</h3>
          <p className="text-sm text-white/50 mt-1 max-w-2xl">
            White-label this deployment for a different vertical. Everything below drives the storefront branding,
            emails, QR codes and photo watermarks.
          </p>
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-1">Load preset</label>
          <select
            value=""
            onChange={(e) => loadPreset(e.target.value)}
            className="input-minimal bg-black !w-56"
            data-testid="identity-preset-select"
          >
            <option value="">Choose a vertical…</option>
            {Object.keys(presets).map((k) => (
              <option key={k} value={k}>{presets[k].site_name_primary}{presets[k].site_name_accent} ({presets[k].site_domain})</option>
            ))}
          </select>
        </div>
      </div>

      <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5">
        {FIELDS.map(([key, label, ph]) => (
          <div key={key} className={key === "categories_strip" || key === "tagline" ? "md:col-span-2" : ""}>
            <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-1">{label}</label>
            <input
              value={form[key] || ""}
              onChange={(e) => set(key, e.target.value)}
              placeholder={ph}
              className="input-minimal"
              data-testid={`identity-${key}`}
            />
          </div>
        ))}
        <div className="md:col-span-2">
          <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-1">Tagline (hero paragraph)</label>
          <textarea
            value={form.tagline || ""}
            onChange={(e) => set("tagline", e.target.value)}
            rows={2}
            className="input-minimal resize-none"
            data-testid="identity-tagline"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-1">Accent colour</label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={form.accent_color || "#FF6A00"}
              onChange={(e) => { set("accent_color", e.target.value); applyAccent(e.target.value); }}
              className="h-10 w-16 bg-transparent border border-white/20 cursor-pointer"
              data-testid="identity-accent-color"
            />
            <span className="font-condensed text-sm text-white/60">{form.accent_color}</span>
            <span className="pm-slash text-xs"><span>Live preview</span></span>
          </div>
        </div>
        <div className="flex items-end">
          <button
            type="submit"
            disabled={saving}
            className="bg-white text-black px-8 py-3 text-xs uppercase tracking-[0.3em] hover:bg-white/90 disabled:opacity-50"
            data-testid="identity-save"
          >
            {saving ? "Saving…" : "Save site identity"}
          </button>
        </div>
      </form>
      <p className="text-xs text-white/40 font-condensed">
        Upload a custom nav logo, watermark icon and hero photos for this vertical in the "Brand assets"
        section below. Already-uploaded photos keep their original watermark.
      </p>
    </div>
  );
}
